// =========================================================================
// Progreso · objetivos de peso y trayectoria esperada
//
// Se calcula UNA VEZ, al terminar el onboarding, y se dibuja desde el día 0
// en las gráficas para que la persona vea a dónde va, no solo dónde está.
//
// ── DÓNDE ESTÁ LA RAYA (decisión de Carlos, 9 de agosto de 2026) ─────────
// Lo que produce este módulo son OBJETIVOS TEÓRICOS DE REFERENCIA, con fin
// educativo: sirven para que la persona entienda su propia necesidad y el
// orden de magnitud del recorrido. NO son decisiones clínicas personalizadas
// ni consecuencia de una intervención médica.
//
// Este módulo hace ARITMÉTICA sobre datos declarados: talla, peso, edad y
// sexo. No interpreta síntomas, no indica tratamiento y no clasifica a nadie
// en una categoría clínica.
//
// Las comorbilidades SE REGISTRAN y se devuelven, pero NO desplazan el peso
// objetivo con ningún coeficiente: ajustar un objetivo ponderal por patología
// cruza la prueba operativa del nivel 2 de `acompanamiento-definicion.md`
// («¿cambiaría la recomendación si tuviera otra enfermedad o medicación?»).
// Lo que hacen es DISPARAR UNA DERIVACIÓN: cuando hay patología registrada,
// el plan recomienda solicitar valoración médica para fijar cuál es la
// pérdida ponderal adecuada en ese caso concreto. Ese número lo fija el
// profesional, no este código.
//
// ── PENDIENTE DE VALIDACIÓN CLÍNICA (T3/T4 de Carlos) ────────────────────
// La tabla `RANGO_IMC_POR_EDAD` reproduce el rango saludable ajustado por
// edad de uso extendido en geriatría (línea Andres). NO está validada por
// Carlos todavía. Hasta que lo esté, el plan no debe mostrarse a un paciente.
// =========================================================================
import type { PuntoSerie } from './tipos';

export type Sexo = 'hombre' | 'mujer';

/** Ritmo de pérdida considerado adecuado. Es un RANGO, no una cifra. */
export const RITMO_MIN_KG_SEMANA = 0.5;
export const RITMO_MAX_KG_SEMANA = 1.0;

/** Cada fase de pérdida dura 3 meses. */
export const SEMANAS_POR_FASE = 13;
/**
 * Cada fase persigue el 30 % de la pérdida total. Es un objetivo razonable
 * por trimestre: al tercer trimestre se lleva el 90 %, y el resto cae en una
 * cuarta fase corta.
 */
export const FRACCION_POR_FASE = 0.3;

/**
 * Rango de IMC saludable por tramo de edad. El límite superior sube con los
 * años: en edad avanzada un IMC bajo se asocia a peor pronóstico, así que
 * aplicar el 18,5-24,9 de un adulto joven a los 70 empujaría a alguien a
 * adelgazar por debajo de donde le conviene.
 * PENDIENTE DE VALIDACIÓN CLÍNICA.
 */
export const RANGO_IMC_POR_EDAD: { hasta: number; min: number; max: number }[] = [
  { hasta: 24, min: 19, max: 24 },
  { hasta: 34, min: 20, max: 25 },
  { hasta: 44, min: 21, max: 26 },
  { hasta: 54, min: 22, max: 27 },
  { hasta: 64, min: 23, max: 28 },
  { hasta: Infinity, min: 24, max: 29 },
];

/**
 * Umbrales de perímetro abdominal por sexo (criterio europeo). Es el único
 * punto donde el sexo cambia un número: el reparto de grasa difiere, y el
 * mismo perímetro no significa lo mismo en un hombre y en una mujer.
 */
export const CINTURA_OBJETIVO_CM: Record<Sexo, number> = {
  hombre: 94,
  mujer: 80,
};

export function rangoImcPorEdad(edad: number): { min: number; max: number } {
  const tramo = RANGO_IMC_POR_EDAD.find((t) => edad <= t.hasta);
  return { min: tramo!.min, max: tramo!.max };
}

export interface PerfilAntropometrico {
  edad: number;
  /**
   * Opcional a propósito. Si no consta, NO se elige uno por defecto: el
   * umbral de cintura difiere 14 cm entre sexos, así que suponerlo le
   * enseñaría a media base de usuarias un objetivo que no es el suyo.
   * Sin sexo declarado simplemente no hay línea de cintura.
   */
  sexo?: Sexo;
  pesoKg: number;
  tallaCm: number;
  cinturaCm?: number;
  /** Se registran y se devuelven. NO mueven el peso objetivo. */
  comorbilidades?: string[];
}

/**
 * Una fase de pérdida: un trimestre con su propio objetivo. Se trabaja fase a
 * fase porque «perder 24 kg» no es un objetivo, es una condena; «perder 7 kg
 * en tres meses» sí se puede sostener.
 */
export interface Fase {
  /** 1, 2, 3… */
  numero: number;
  etiqueta: string;
  /** Semanas desde el día 0 en las que transcurre la fase. */
  desdeSemana: number;
  hastaSemana: number;
  fechaInicio: string;
  fechaFin: string;
  /** Peso al que se aspira al terminar la fase. */
  pesoObjetivoKg: number;
  /** Pérdida DE ESTA fase, no acumulada. */
  perdidaKg: number;
  /** Pérdida acumulada al terminarla. */
  perdidaAcumuladaKg: number;
  /**
   * false cuando el objetivo de la fase exigiría pasar de 1 kg/semana. En ese
   * caso se ha RECORTADO al ritmo máximo seguro y el plan añade más fases, en
   * vez de mostrar un objetivo que solo se cumple pasándose.
   */
  factible: boolean;
}

/** Un tramo de la banda esperada: mismo día, dos pesos según el ritmo. */
export interface TramoBanda {
  fecha: string;
  /** Peso al ritmo rápido, 1 kg/semana. Es el borde INFERIOR. */
  rapido: number;
  /** Peso al ritmo lento, 0,5 kg/semana. Es el borde SUPERIOR. */
  lento: number;
}

export interface PlanObjetivo {
  imcActual: number;
  rangoImcSaludable: { min: number; max: number };
  /** true si ya está dentro del rango: entonces NO hay objetivo de pérdida. */
  yaEnRango: boolean;
  pesoObjetivoKg: number;
  perdidaTotalKg: number;
  /** null cuando no consta el sexo: no se supone ninguno. */
  cinturaObjetivoCm: number | null;
  /**
   * Por qué hay (o no hay) recorrido de pérdida. Es DESCRIPTIVO de lo que la
   * persona ha declarado, no una indicación: quien tiene patología declarada
   * recibe además la derivación.
   */
  motivo: 'ya-en-rango' | 'con-patologia-declarada' | 'por-antropometria';
  fases: Fase[];
  banda: TramoBanda[];
  /**
   * Pérdida del 5-10 % del peso, que es donde la evidencia sitúa el beneficio
   * metabólico. Se devuelve SIEMPRE como contexto; cuando hay comorbilidad
   * registrada es la cifra que el profesional suele priorizar sobre el peso
   * "normal", pero quién decide eso es él.
   */
  perdidaRelevanteKg: { min: number; max: number };
  comorbilidades: string[];
  /**
   * Presente SIEMPRE que haya patología registrada. No es un aviso decorativo:
   * es la frontera del producto: con una enfermedad de por medio, cuál es la
   * pérdida adecuada deja de ser una cuenta y pasa a ser una valoración.
   */
  derivacion?: string;
  /**
   * Resultado de la valoración inicial. Con patología declarada NO se admite
   * automáticamente: pasa a revisión humana, que decide si la persona es
   * adecuada para el programa y si precisa seguimiento.
   *
   * Mientras esté pendiente, las cifras del plan son provisionales y las
   * pantallas deben decirlo. Ver `PLAN_PROVISIONAL`.
   */
  estadoAdmision: 'admitido' | 'pendiente-revision-humana';
  /**
   * Carácter de las cifras. Va con el plan a todas partes para que ninguna
   * pantalla pueda presentarlas como indicación clínica por descuido.
   */
  caracter: 'referencia-educativa';
  /** Presente si un profesional revisó el caso y levantó un bloqueo. */
  revision?: AprobacionHumana;
  /** Motivo por el que no hay plan de pérdida, si es el caso. */
  nota?: string;
}

/**
 * Qué patologías declaradas abren revisión humana.
 *
 * El valor por defecto es CUALQUIERA: falla cerrando. Que una hipertensión
 * bien controlada no deba frenar el alta es probablemente cierto, pero cuál
 * es el subconjunto "relevante" es una decisión clínica de Carlos, y mientras
 * no esté tomada es más barato revisar de más que admitir de menos.
 * PENDIENTE T3/T4: acotar la lista.
 */
export function exigeRevisionHumana(comorbilidades: string[]): boolean {
  return comorbilidades.some((c) => c.trim().length > 0);
}

export const PLAN_PROVISIONAL =
  'Tu valoración inicial está pendiente de revisión por un profesional. Hasta ' +
  'entonces estas cifras son provisionales: se confirmarán, se ajustarán o se ' +
  'sustituirán por las que indique la revisión.';

export const AVISO_DERIVACION =
  'Has registrado alguna enfermedad. Estas cifras son orientativas y educativas: ' +
  'cuál es la pérdida de peso adecuada en tu caso debe valorarlo un profesional. ' +
  'Solicita una valoración médica antes de fijar tu objetivo.';

// ── LÍNEA ROJA: trastorno de la conducta alimentaria ─────────────────────
// Decisión de Carlos, 9 de agosto de 2026. Es INFRANQUEABLE:
//
//   El reporte O LA SOSPECHA de un trastorno de la conducta alimentaria
//   bloquea el sistema. Pasa a valoración humana inmediata, NO permite
//   continuar el proceso y NO SE REALIZA NINGÚN CÁLCULO sin aprobación
//   previa de un profesional.
//
// Por qué así y no como un aviso: enseñarle a alguien con un TCA un objetivo
// de pérdida de peso, una curva descendente y un plazo es darle una diana. El
// daño no lo evita una advertencia al pie; lo evita no calcular.
//
// Por eso esta regla NO es un campo más del plan: `calcularPlan` es privada y
// la única entrada pública es `valorar()`, que devuelve una unión discriminada.
// Quien quiera un número tiene que atender antes el caso bloqueado — no se
// puede olvidar, porque no compila.

export type MotivoBloqueo =
  | 'tca-declarado'
  | 'tca-sospecha-texto'
  | 'antropometria-por-debajo-del-rango';

/** Identificador de TCA en el catálogo de comorbilidades. */
export const ID_TCA = 'tca';

/**
 * Términos que hacen sospechar un TCA en el texto libre. La lista es amplia a
 * propósito: un falso positivo cuesta una revisión humana de más, y un falso
 * negativo cuesta enseñarle a alguien con un TCA su plan de adelgazamiento.
 * La asimetría entre esos dos errores es todo el diseño de esta regla.
 */
export const TERMINOS_SOSPECHA_TCA = [
  'anorexia', 'anorexico', 'anorexica', 'bulimia', 'bulimico', 'bulimica',
  'tca', 'trastorno alimentario', 'trastorno de la conducta alimentaria',
  'atracon', 'atracones', 'purga', 'purgas', 'purgarme',
  'vomito', 'vomitos', 'vomitar', 'laxante', 'laxantes', 'diuretico',
  'ortorexia', 'vigorexia', 'ayuno prolongado', 'restriccion alimentaria',
  'conducta alimentaria',
];

/** Minúsculas y sin tildes, para que «atracón» case con «atracon». */
function normalizarTexto(t: string): string {
  return t.normalize('NFD').replace(/[\u0300-\u036f]/g, '').toLowerCase();
}

export function haySospechaEnTexto(comorbilidades: string[]): boolean {
  const texto = normalizarTexto(comorbilidades.join(' '));
  return TERMINOS_SOSPECHA_TCA.some((t) => texto.includes(normalizarTexto(t)));
}

export const MENSAJE_BLOQUEO =
  'Antes de seguir, queremos que hable contigo un profesional. Por lo que nos has ' +
  'contado, fijar un objetivo de peso automáticamente no sería adecuado ni seguro ' +
  'en tu caso, así que no vamos a calcular ninguno. Nos pondremos en contacto para ' +
  'una valoración. No has hecho nada mal: es exactamente para esto para lo que ' +
  'preguntamos.';

export const CARACTER_REFERENCIA =
  'Valores teóricos de referencia con fin educativo. No son una indicación clínica ' +
  'personalizada ni sustituyen la valoración de un profesional.';

function sumarSemanas(desdeIso: string, semanas: number): string {
  const d = new Date(`${desdeIso}T00:00:00Z`);
  d.setUTCDate(d.getUTCDate() + Math.round(semanas * 7));
  return d.toISOString().slice(0, 10);
}

function redondear(n: number): number {
  return Math.round(n * 10) / 10;
}

/**
 * Calcula el plan. `dia0` se inyecta (ISO, solo fecha) para que las pruebas
 * no dependan del reloj.
 */
function calcularPlan(perfil: PerfilAntropometrico, dia0: string): PlanObjetivo | null {
  const { edad, sexo, pesoKg, tallaCm } = perfil;
  if (!(pesoKg > 0) || !(tallaCm > 0) || !(edad > 0)) return null;

  const m = tallaCm / 100;
  const imcActual = redondear(pesoKg / (m * m));
  const rango = rangoImcPorEdad(edad);
  const comorbilidades = perfil.comorbilidades ?? [];
  const cinturaObjetivoCm = sexo ? CINTURA_OBJETIVO_CM[sexo] : null;
  const perdidaRelevanteKg = {
    min: redondear(pesoKg * 0.05),
    max: redondear(pesoKg * 0.1),
  };

  // El objetivo es el LÍMITE SUPERIOR del rango saludable, no su punto medio:
  // es el peso normal más cercano, y por tanto el más alcanzable. Apuntar al
  // centro añadiría kilos que nadie ha pedido perder.
  const pesoObjetivoKg = redondear(rango.max * m * m);

  const base = {
    imcActual,
    rangoImcSaludable: rango,
    cinturaObjetivoCm,
    perdidaRelevanteKg,
    comorbilidades,
    caracter: 'referencia-educativa' as const,
    estadoAdmision: exigeRevisionHumana(comorbilidades)
      ? ('pendiente-revision-humana' as const)
      : ('admitido' as const),
    ...(comorbilidades.length > 0 ? { derivacion: AVISO_DERIVACION } : {}),
  };

  // Ya está dentro del rango: no se fabrica un objetivo de pérdida. Empujar a
  // adelgazar a quien no lo necesita es hacer daño, no acompañar.
  if (imcActual <= rango.max) {
    return {
      ...base,
      yaEnRango: true,
      motivo: 'ya-en-rango' as const,
      pesoObjetivoKg: redondear(pesoKg),
      perdidaTotalKg: 0,
      fases: [],
      banda: [],
      nota: 'El peso ya está dentro del rango saludable para su edad. El programa trabaja hábitos y cintura, no pérdida de peso.',
    };
  }

  const perdidaTotalKg = redondear(pesoKg - pesoObjetivoKg);

  // ── Fases: 30 % de la pérdida por trimestre ────────────────────────────
  // Se trabaja por fases porque "perder 24 kg" no es un objetivo sostenible;
  // "perder 7 kg en tres meses" sí. Cada fase persigue el 30 % del total, así
  // que al tercer trimestre se lleva el 90 % y el resto cae en una cuarta.
  const objetivoPorFase = redondear(perdidaTotalKg * FRACCION_POR_FASE);
  // Techo de seguridad: ninguna fase puede exigir más de 1 kg/semana. Si el
  // 30 % se pasa, se RECORTA la fase y el plan añade las que hagan falta.
  const techoPorFase = RITMO_MAX_KG_SEMANA * SEMANAS_POR_FASE;

  const fases: Fase[] = [];
  let acumulada = 0;
  let numero = 0;
  // El tope de iteraciones es un cinturón: sin él, un objetivo por fase de 0
  // por redondeo daría un bucle infinito.
  while (acumulada < perdidaTotalKg - 0.05 && numero < 40) {
    numero += 1;
    const restante = redondear(perdidaTotalKg - acumulada);
    const bruta = Math.min(objetivoPorFase, restante);
    const perdidaKg = redondear(Math.min(bruta, techoPorFase));
    if (perdidaKg <= 0) break;
    acumulada = redondear(acumulada + perdidaKg);
    fases.push({
      numero,
      etiqueta: `Fase ${numero}`,
      desdeSemana: (numero - 1) * SEMANAS_POR_FASE,
      hastaSemana: numero * SEMANAS_POR_FASE,
      fechaInicio: sumarSemanas(dia0, (numero - 1) * SEMANAS_POR_FASE),
      fechaFin: sumarSemanas(dia0, numero * SEMANAS_POR_FASE),
      pesoObjetivoKg: redondear(pesoKg - acumulada),
      perdidaKg,
      perdidaAcumuladaKg: acumulada,
      factible: bruta <= techoPorFase,
    });
  }
  if (fases.length > 0) fases[fases.length - 1].etiqueta = 'Peso objetivo';

  // ── Banda esperada, semana a semana desde el día 0 ─────────────────────
  // Dos bordes, no una línea: una línea única prometería una fecha exacta que
  // nadie puede cumplir. Ambos bordes se aplanan al llegar al objetivo.
  const semanasBanda = Math.ceil(perdidaTotalKg / RITMO_MIN_KG_SEMANA);
  const banda: TramoBanda[] = Array.from({ length: semanasBanda + 1 }, (_, s) => ({
    fecha: sumarSemanas(dia0, s),
    rapido: redondear(Math.max(pesoKg - RITMO_MAX_KG_SEMANA * s, pesoObjetivoKg)),
    lento: redondear(Math.max(pesoKg - RITMO_MIN_KG_SEMANA * s, pesoObjetivoKg)),
  }));

  return {
    ...base,
    yaEnRango: false,
    motivo: comorbilidades.length > 0 ? 'con-patologia-declarada' : 'por-antropometria',
    pesoObjetivoKg,
    perdidaTotalKg,
    fases,
    banda,
  };
}

// ── Entrada pública ───────────────────────────────────────────────────────

/**
 * Decisión de un profesional sobre un caso bloqueado. Es lo que permite
 * levantar la línea roja cuando la señal era un falso positivo o no aplica.
 *
 * `profesional` y `motivo` son obligatorios y no se rellenan solos: un
 * desbloqueo sin autor ni razón no es una decisión clínica, es un agujero. Si
 * el día de mañana hay que responder por qué a esta persona se le calculó un
 * objetivo, la respuesta tiene que estar aquí.
 */
export interface AprobacionHumana {
  decision: 'desbloquear' | 'mantener-bloqueo';
  /** Identificador del profesional que decide. */
  profesional: string;
  /** ISO 8601. */
  fecha: string;
  /** Por qué. En un desbloqueo, qué descartó la sospecha. */
  motivo: string;
}

export interface ValoracionBloqueada {
  estado: 'bloqueada';
  motivos: MotivoBloqueo[];
  mensaje: string;
  /**
   * false cuando un profesional ya revisó el caso y decidió MANTENER el
   * bloqueo. Entonces deja de ser una señal automática revisable y pasa a ser
   * una decisión tomada.
   */
  desbloqueable: boolean;
  /** Presente si un profesional ya se pronunció. */
  revision?: AprobacionHumana;
}
export interface ValoracionCalculada {
  estado: 'calculada';
  plan: PlanObjetivo;
}
/** Faltan datos para calcular nada. No es un bloqueo: es un hueco. */
export interface ValoracionIncompleta {
  estado: 'incompleta';
}
export type Valoracion = ValoracionBloqueada | ValoracionCalculada | ValoracionIncompleta;

/**
 * Decide si el caso queda bloqueado. Se ejecuta ANTES que cualquier cuenta.
 * Se exporta para poder consultarlo desde el formulario, que tiene que parar
 * el proceso en el momento, sin esperar a que haya un plan.
 */
export function evaluarBloqueo(perfil: PerfilAntropometrico): ValoracionBloqueada | null {
  const comorbilidades = perfil.comorbilidades ?? [];
  const motivos: MotivoBloqueo[] = [];

  if (comorbilidades.includes(ID_TCA)) motivos.push('tca-declarado');
  if (haySospechaEnTexto(comorbilidades)) motivos.push('tca-sospecha-texto');

  // Señal antropométrica: alguien por DEBAJO del rango saludable pidiendo
  // acompañamiento de pérdida de peso. Puede no ser un TCA, y da igual: ese
  // caso lo mira una persona antes de que el sistema diga una sola cifra.
  if (perfil.pesoKg > 0 && perfil.tallaCm > 0 && perfil.edad > 0) {
    const m = perfil.tallaCm / 100;
    const imcActual = perfil.pesoKg / (m * m);
    if (imcActual < rangoImcPorEdad(perfil.edad).min) {
      motivos.push('antropometria-por-debajo-del-rango');
    }
  }

  if (motivos.length === 0) return null;
  // Se deduplica por si un mismo caso dispara dos veces la misma señal.
  return {
    estado: 'bloqueada',
    motivos: [...new Set(motivos)],
    mensaje: MENSAJE_BLOQUEO,
    desbloqueable: true,
  };
}

/** Un desbloqueo solo cuenta si lleva autor y motivo. */
export function aprobacionValida(a: AprobacionHumana | undefined): boolean {
  return !!a && a.profesional.trim().length > 0 && a.motivo.trim().length > 0;
}

/**
 * ÚNICA entrada pública. `calcularPlan` es privada a propósito: así no se
 * puede obtener una cifra sin haber atendido antes el caso bloqueado, porque
 * la unión discriminada no compila si no se distingue el estado.
 *
 * El orden importa y no se puede invertir: el bloqueo se evalúa PRIMERO y
 * corta. Con TCA reportado o sospechado no se calcula nada, ni siquiera para
 * guardarlo sin enseñarlo.
 *
 * `revision` es la salida: un profesional puede levantar el bloqueo cuando la
 * señal era un falso positivo o no aplica. Solo un desbloqueo CON autor y
 * motivo lo levanta; uno sin ellos se ignora y el caso sigue bloqueado, que es
 * el fallo seguro.
 */
export function valorar(
  perfil: PerfilAntropometrico,
  dia0: string,
  revision?: AprobacionHumana,
): Valoracion {
  const bloqueo = evaluarBloqueo(perfil);

  if (bloqueo) {
    const desbloqueado = revision?.decision === 'desbloquear' && aprobacionValida(revision);
    if (!desbloqueado) {
      return {
        ...bloqueo,
        // Revisado y mantenido deja de ser una señal automática pendiente:
        // es una decisión tomada, y no se vuelve a ofrecer como revisable.
        desbloqueable: revision?.decision !== 'mantener-bloqueo',
        ...(revision ? { revision } : {}),
      };
    }
  }

  const plan = calcularPlan(perfil, dia0);
  if (!plan) return { estado: 'incompleta' };

  // Si un profesional ha revisado y desbloqueado, la revisión humana que
  // exigía la patología YA se ha hecho: sería absurdo seguir diciendo que
  // está pendiente de lo que acaba de ocurrir.
  const revisado = revision?.decision === 'desbloquear' && aprobacionValida(revision);
  return {
    estado: 'calculada',
    plan: revisado
      ? { ...plan, estadoAdmision: 'admitido', revision }
      : plan,
  };
}

/** La banda, en la forma que consume la gráfica. */
export function bandaComoSeries(plan: PlanObjetivo): {
  rapido: PuntoSerie[];
  lento: PuntoSerie[];
} {
  const punto = (t: TramoBanda, valor: number): PuntoSerie => ({
    fecha: t.fecha,
    valor,
    etiqueta: `${t.fecha.slice(8, 10)}/${t.fecha.slice(5, 7)}`,
  });
  return {
    rapido: plan.banda.map((t) => punto(t, t.rapido)),
    lento: plan.banda.map((t) => punto(t, t.lento)),
  };
}
